






import { TimeSelection, IDateGroup } from "../models";
import { ISelectListItem } from "@everbright/promotions/lib/models";
import moment from "moment";
import Vue from "vue";
import Component from "vue-class-component";
import { Prop, Watch } from "vue-property-decorator";

/**
 * A dictionary where the key is a `DashboardGridTimeSelection` and the value is a `(now: Date) => IDateGroup`
 * This dictionary is used to take a selection from the b-form-select (in the template above) and calculate a date range from it.
 * See the `TimeSelection` enum for the available options.
 * If an option is added to the enum, the build will fail if it isn't also added here.
 */
const dateCalculations: Record<TimeSelection, (now: Date) => IDateGroup> = {
    [TimeSelection.Today]: now => ({
        startDate: now,
        endDate: now
    }),

    [TimeSelection.Yesterday]: now => ({
        startDate: moment(now)
            .subtract(1, "day")
            .toDate(),
        endDate: moment(now)
            .subtract(1, "day")
            .toDate()
    }),

    [TimeSelection.ThisWeek]: now => ({
        startDate: moment(now)
            .startOf("week")
            .toDate(),
        endDate: now
    }),

    [TimeSelection.Last7Days]: now => ({
        startDate: moment(now)
            .subtract(7, "days")
            .toDate(),
        endDate: now
    }),

    [TimeSelection.LastWeek]: now => ({
        startDate: moment(now)
            .startOf("week")
            .subtract(1, "day")
            .startOf("week")
            .toDate(),
        endDate: moment(now)
            .startOf("week")
            .subtract(1, "day")
            .toDate()
    }),

    [TimeSelection.Last14Days]: now => ({
        startDate: moment(now)
            .subtract(14, "day")
            .toDate(),
        endDate: now
    }),

    [TimeSelection.ThisMonth]: now => ({
        startDate: moment(now)
            .startOf("month")
            .toDate(),
        endDate: now
    }),

    [TimeSelection.Last30Days]: now => ({
        startDate: moment(now)
            .subtract(30, "day")
            .toDate(),
        endDate: now
    }),

    [TimeSelection.LastMonth]: now => ({
        startDate: moment(now)
            .startOf("month")
            .subtract(1, "d")
            .startOf("month")
            .toDate(),
        endDate: moment(now)
            .startOf("month")
            .subtract(1, "d")
            .toDate()
    }),

    [TimeSelection.ThisYear]: now => ({
        startDate: moment(now)
            .startOf("year")
            .toDate(),
        endDate: now
    }),

    [TimeSelection.LastYear]: now => ({
        startDate: moment(now)
            .startOf("year")
            .subtract(1, "day")
            .startOf("year")
            .toDate(),
        endDate: moment(now)
            .startOf("year")
            .subtract(1, "day")
            .toDate()
    })
};

@Component
export default class DateViewSelector extends Vue {
    currentDate = new Date();
    internalTimeSelection: TimeSelection | null = null;

    @Prop()
    value: IDateGroup | null = null;

    get internalValue(): IDateGroup | null {
        return this.value;
    }
    set internalValue(val: IDateGroup | null) {
        this.$emit("input", val);
    }

    @Watch("value")
    valueChanged() {
        if (!this.value) {
            this.internalTimeSelection = null;
        }
    }

    /**
     * Gets the time range the user selected (ex: Today) that is used to return a date group to the v-model.
     * An example date group thats returned for "Today" would be: 9/15/2021 to 9/15/2021
     * An example date group thats returned for "Last week" would be 9/13/2021 to 9/15/2021
     */
    get timeSelection(): TimeSelection | null {
        return this.internalTimeSelection;
    }
    set timeSelection(val: TimeSelection | null) {
        // If a time was selected, assign it to our internal value:
        this.internalTimeSelection = val;

        // Then, calculate the date range and give it to the parent component:
        this.internalValue = val
            ? dateCalculations[val](this.currentDate)
            : null;
    }

    get dateViewOptions(): ISelectListItem[] {
        // Map the dateCalculations object to a select list:
        return Object.keys(dateCalculations).map(k => ({ text: k, value: k }));
    }

    mounted() {
        // Default selection is today:
        this.timeSelection = TimeSelection.Today;
    }
}
